import React from 'react';

import { Alert, Box, Typography } from '@mui/material';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import { productTypesExpectedFields } from '~/services/PromoService';

type DisplayField =
  | 'productType'
  | 'originAirportCode'
  | 'destinationAirportCode'
  | 'fareType'
  | 'transactionKey'
  | 'hotelInfo'
  | 'flightInfo';

type PromoItemFieldProps = {
  field: keyof definitions['Discount Request']['order']['items']['0'] | DisplayField;
  categoryBK: definitions['Discount Request']['order']['items']['0']['categoryBK'];
  subCategoryBK?: definitions['Discount Request']['order']['items']['0']['subCategoryBK'];
  title?: string;
  value: string | number | boolean | null | object | undefined | Array<object>;
  longDescription?: string;
  displayOverride?: React.ReactElement;
  forceDisplay?: boolean;
  dependentItemFields?: Array<string>;
};

export const isExpectedField = (
  category: definitions['Discount Request']['order']['items']['0']['categoryBK'],
  subCategory: definitions['Discount Request']['order']['items']['0']['subCategoryBK'] | undefined,
  field: keyof definitions['Discount Request']['order']['items']['0'] | DisplayField,
) => {
  const categoryFields = productTypesExpectedFields[category];
  if (!categoryFields) {
    return false;
  }

  // Try specific subCategory first, then fall back to '*' (default for category)
  const match = (subCategory && categoryFields[subCategory]) || categoryFields['*'];

  if (!match) {
    return false;
  }

  if (field === 'placeId' || field === 'lePlaceId') {
    return false;
  }

  return (match as Record<string, boolean>)[field as string] ?? false;
};

function PromoItemField({
  field,
  categoryBK,
  subCategoryBK,
  value,
  title,
  longDescription,
  displayOverride,
  forceDisplay = false,
  dependentItemFields = [],
}: PromoItemFieldProps) {
  const isEmpty = value == null || value === '';
  const isFieldExpected = isExpectedField(categoryBK, subCategoryBK, field);
  const shouldShowMissingFieldAlert = isFieldExpected && isEmpty;
  const isDependentField = dependentItemFields.some(
    (dependentField) => dependentField.trim().toLowerCase() === String(field).trim().toLowerCase(),
  );
  const missingFieldAlertSeverity: 'warning' | 'error' = isDependentField ? 'error' : 'warning';

  const renderMissingFieldAlert = () => (
    <Alert
      severity={missingFieldAlertSeverity}
      title={`${field} not mapped on item`}
      sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { py: 0 } }}
    >
      {`${field} not mapped on item`}
    </Alert>
  );

  const renderPresentValue = () => {
    if (displayOverride) {
      return displayOverride;
    }
    let formattedValue: React.ReactNode;
    if (typeof value === 'object') {
      formattedValue = value === null ? null : JSON.stringify(value);
    } else if (typeof value === 'boolean') {
      formattedValue = String(value);
    } else {
      formattedValue = value;
    }
    return (
      <Box component="span" title={longDescription}>
        <>
          {title && (
            <Typography component="span" fontWeight="bold">
              {`${title}:`}
            </Typography>
          )}
          {formattedValue}
        </>
      </Box>
    );
  };

  if (!isFieldExpected && !forceDisplay) {
    return null;
  }

  if (shouldShowMissingFieldAlert) {
    return renderMissingFieldAlert();
  }

  return renderPresentValue();
}

export default PromoItemField;
